<template>
  <div class="product-block" :class="[variant, { 'is-main': isMainProduct }]">
    <meta charset="UTF-8">
    <!-- Header zone -->
    <div v-if="header" class="header-zone w-full">
      <img
        :src="getHeaderImageUrl(header.imageName)"
        :alt="header.name"
        class="w-full h-32 object-cover"
      >
    </div>

    <div class="product-content">
      <!-- Bouton de suppression -->
      <button
        class="remove-button"
        @click="
          () => {
            resetProductData();
            $emit('remove');
          }
        "
      >
        <span class="text-red-600">×</span>
      </button>

      <!-- Product zone -->
      <div class="product-main" @dragover.prevent @drop.prevent="onProductDrop">
        <!-- Image container -->
        <div class="image-container" :style="{ background: backgroundColor }">
          <img
            v-if="product?.product_image?.image_path || product?.product_image?.image_name || product?.image"
            align="center"
            :src="
              product?.product_image?.image_path || product?.product_image?.image_name
                ? getProductImageUrl(product.product_image.image_path || product.product_image.image_name)
                : product?.image
            "
            :alt="product?.name"
            :class="[
              'product-image',
              { 'remove-background': removeBackground },
            ]"
          >
          <template v-else>
            <div v-if="!isPreview" class="empty-state">
              Déposez un produit ici
            </div>
            <div v-else class="empty-state-preview">
              <!-- Placeholder SVG pour le mode aperçu -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 100 100"
                class="default-product-svg"
                aria-hidden="true"
                focusable="false"
              >
                <rect width="100" height="100" fill="#f3f4f6" />
                <path
                  d="M85 20H15C12.24 20 10 22.24 10 25V75C10 77.76 12.24 80 15 80H85C87.76 80 90 77.76 90 75V25C90 22.24 87.76 20 85 20ZM80 70H20V30H80V70Z"
                  fill="#d1d5db"
                />
                <circle
                  cx="35"
                  cy="45"
                  r="5"
                  fill="#d1d5db"
                  class="product-svg-circle"
                />
                <path d="M70 55L50 75L40 65L20 85H80L70 55Z" fill="#e5e7eb" />
              </svg>
            </div>
          </template>
          <!-- Toggle background removal -->
          <button
            v-if="product?.image"
            class="background-toggle"
            :title="
              removeBackground ? 'Restaurer le fond' : 'Supprimer le fond'
            "
            @click="toggleBackground"
          >
            <span class="icon">{{ removeBackground ? "🎨" : "✨" }}</span>
          </button>
          <!-- Background color toggle -->
          <button
            class="background-color-toggle"
            :title="'Changer la couleur du fond'"
            @click="toggleColorPicker"
          >
            <span class="icon">🎨</span>
          </button>
          <!-- Color picker -->
          <div v-if="showColorPicker" class="color-picker" @click.stop>
            <div class="color-options">
              <button
                v-for="color in predefinedColors"
                :key="color.value"
                class="color-option"
                :style="{ background: color.value }"
                :title="color.name"
                @click="selectBackgroundColor(color.value)"
              />
            </div>
            <input
              v-model="customColor"
              type="color"
              class="custom-color-input"
              @change="selectBackgroundColor(customColor)"
            >
          </div>
          <!-- Pictogram zone -->
          <div class="picto-zone" @dragover.prevent @drop.prevent="onPictoDrop">
            <img
              v-if="pictogram"
              :src="getPictoImageUrl(pictogram.image_name)"
              :alt="pictogram.name"
              class="picto-image"
              :style="{ transform: `scale(${pictoScale})` }"
            >
            <!-- Contrôles de taille -->
            <div v-if="pictogram" class="picto-controls">
              <button class="size-control" @click="adjustPictoSize(-0.1)">
                -
              </button>
              <button class="size-control" @click="adjustPictoSize(0.1)">
                +
              </button>
              <button class="remove-picto" @click="removePicto">
                <span class="text-red-600">×</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Product info -->
        <div class="product-info">
          <div class="title-container">
            <div
              v-if="!isPreview || (product?.name && product.name.trim())"
              class="product-title"
              :style="{ transform: `scale(${titleScale})`, transformOrigin: 'left' }"
              :contenteditable="!isPreview"
              @blur="updateTitle"
              v-text="product?.name ? product.name.toUpperCase() : ''"
            />
            <!-- Contrôles de taille du titre -->
            <div v-if="!isPreview" class="title-controls">
              <button class="size-control" @click="adjustTitleSize(-0.1)">
                -
              </button>
              <button class="size-control" @click="adjustTitleSize(0.1)">
                +
              </button>
            </div>
          </div>

          <div
            class="product-description"
            :class="{
              'empty-description':
                isPreview &&
                (!product?.description || !product.description.trim()),
            }"
            :contenteditable="!isPreview"
            @blur="updateDescription"
            @keydown.enter="handleEnterKey"
            v-html="formatDescription(product?.description || '')"
          />

          <div class="price-section">
            <div class="price-actions">
              <div v-if="!isPreview && showPrice" class="price-controls">
                <button class="size-control" @click="adjustPriceSize(-0.1)">
                  -
                </button>
                <button class="size-control" @click="adjustPriceSize(0.1)">
                  +
                </button>
                <button
                  v-if="!isPreview"
                  class="toggle-old-price-button"
                  :title="
                    showOldPriceComputed
                      ? 'Masquer le prix barré'
                      : 'Afficher le prix barré'
                  "
                  @click="toggleOldPrice"
                >
                  <span class="icon">{{
                    showOldPriceComputed ? "~~" : "~~"
                  }}</span>
                </button>
              </div>
              <button
                v-if="!isPreview"
                class="toggle-price-button"
                :title="showPrice ? 'Masquer le prix' : 'Afficher le prix'"
                @click="showPrice = !showPrice"
              >
                <span class="icon">{{ showPrice ? "👁️" : "🚫" }}</span>
              </button>
            </div>
            <div class="price-content" :class="{ hidden: !showPrice }">
              <div class="product-price flex-grow">
                <!-- Prix barré (ancien prix) -->
                <div
                  v-if="
                    showOldPriceComputed && product?.price_old !== undefined
                  "
                  class="price-old-container"
                >
                  <span
                    class="price-old"
                    :class="priceSize"
                    :contenteditable="!isPreview"
                    @blur="updateOldPrice"
                    @keydown.enter.prevent="handlePriceEnter"
                  >
                    {{ formatPrice(product.price_old) }}
                  </span>
                  <span class="price-suffix ht">€HT</span>
                </div>
                <div class="price-container">
                  <div class="price-ht-container">
                    <span
                      class="price-ht"
                      :class="priceSize"
                      :contenteditable="!isPreview"
                      @blur="updatePrice"
                      @keydown.enter.prevent="handlePriceEnter"
                      v-text="formatPrice(product?.price_ht || 0)"
                    />
                    <span class="price-suffix ht" :class="priceSize">€HT</span>
                  </div>
                </div>
                <div class="price-ttc-container">
                  <span class="price-ttc" :class="priceTTCSize">
                    {{ formatPriceTTC(product?.price_ht || 0) }}
                    <span class="ttc-text">€TTC</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Brand zone (en bas à droite) -->
      <div class="brand-zone" @dragover.prevent @drop.prevent="onBrandDrop">
        <img
          v-if="brand"
          :src="getBrandImageUrl(brand.image_name)"
          :alt="brand.name"
          class="brand-image"
          :style="{ transform: `scale(${brandScale})` }"
        >
        <div v-else class="brand-placeholder" />
        <div v-if="brand && !isPreview" class="brand-controls">
          <button class="size-control" @click="adjustBrandSize(-0.1)">
            -
          </button>
          <button class="size-control" @click="adjustBrandSize(0.1)">
            +
          </button>
          <button class="remove-brand" @click="removeBrand">
            <span class="text-red-600">×</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useProductCustomizationStore } from "@/stores/supports/useProductCustomizationStore";
import {
  computed,
  nextTick,
  onMounted,
  ref,
  watch,
  type Ref,
} from "@vue/runtime-core";

interface Product {
  id: number;
  name: string;
  description: string;
  price_ht: number;
  price_old?: number;
  image: string;
  product_image_id: number;
  product_image: {
    image_name: string;
    image_path?: string;
  };
  reference?: string;
  image_name?: string;
  element_settings?: {
    removeBackground?: boolean;
    backgroundColor?: string;
    showPrice?: boolean;
    showOldPrice?: boolean;
    priceScale?: number;
    priceSize?: string;
    priceTTCSize?: string;
    pictogram?: any;
    pictoScale?: number;
    brand?: any;
    brandScale?: number;
    header?: any;
    titleScale?: number;
    position?: any;
    rotation?: number;
    zIndex?: number;
  };
}

const props = defineProps<{
  product: Product | null;
  variant: "columnOne" | "rowOne" | "rowTwo" | "columnTwo";
  isMainProduct?: boolean;
  isPreview?: boolean;
  priceSize?: "price-sm" | "price-md" | "price-lg";
  priceTTCSize?: "price-ttc-sm" | "price-ttc-md" | "price-ttc-lg";
}>();

const emit = defineEmits<{
  (e: "remove"): void;
  (e: "update:product", product: Product): void;
  (e: "update:productData", data: any): void;
}>();

const customizationStore = useProductCustomizationStore();
const priceRef = ref<HTMLElement | null>(null);
const titleRef = ref<HTMLElement | null>(null);

// État local pour chaque instance
const showColorPicker = ref(false);
const customColor = ref("#f8f8f8");
const showPrice = ref(true);

// Récupérer les données de personnalisation du store
const customization = computed(() => {
  if (!props.product?.id) return customizationStore.getDefaultCustomization();
  return customizationStore.getCustomization(props.product.id);
});

// Utiliser les données du store
const removeBackground = computed({
  get: () => customization.value.removeBackground || false,
  set: (value) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, {
        removeBackground: value,
      });
    }
  },
});

const backgroundColor = computed({
  get: () => {
    if (!props.product) {
      return "#f8f8f8";
    }

    // Si nous avons une personnalisation, l'utiliser
    if (
      customization.value.backgroundColor &&
      customization.value.backgroundColor !== "#fff"
    ) {
      return customization.value.backgroundColor;
    }

    // Sinon, utiliser les paramètres du produit
    return props.product?.element_settings?.backgroundColor || "#fff";
  },
  set: (value) => {
    if (props.product?.id) {
      // Si la valeur est la même que celle des paramètres du produit, réinitialiser la personnalisation
      if (value === props.product?.element_settings?.backgroundColor) {
        customizationStore.setCustomization(props.product.id, {
          backgroundColor: "#fff", // Valeur par défaut
        });
      } else {
        customizationStore.setCustomization(props.product.id, {
          backgroundColor: value,
        });
      }
    }
  },
});

// Ajouter un watcher pour initialiser la personnalisation avec les paramètres du produit
watch(
  () => props.product,
  (newProduct) => {
    if (newProduct?.id) {
      const currentCustomization = customizationStore.getCustomization(
        newProduct.id
      );

      // Initialiser backgroundColor si nécessaire
      if (
        newProduct?.element_settings?.backgroundColor &&
        currentCustomization.backgroundColor === "#fff"
      ) {
        customizationStore.setCustomization(newProduct.id, {
          backgroundColor: newProduct.element_settings.backgroundColor,
        });
      }

      // Initialiser pictoScale si nécessaire
      if (
        newProduct?.element_settings?.pictoScale &&
        currentCustomization.pictoScale === 1
      ) {
        customizationStore.setCustomization(newProduct.id, {
          pictoScale: newProduct.element_settings.pictoScale,
        });
      }

      // Initialiser brandScale si nécessaire
      if (
        newProduct?.element_settings?.brandScale &&
        currentCustomization.brandScale === 1
      ) {
        customizationStore.setCustomization(newProduct.id, {
          brandScale: newProduct.element_settings.brandScale,
        });
      }

      // Initialiser pictogram si nécessaire
      if (
        newProduct?.element_settings?.pictogram &&
        !currentCustomization.pictogram
      ) {
        customizationStore.setCustomization(newProduct.id, {
          pictogram: newProduct.element_settings.pictogram,
        });
      }

      // Initialiser brand si nécessaire
      if (
        newProduct?.element_settings?.brand &&
        !currentCustomization.brand
      ) {
        customizationStore.setCustomization(newProduct.id, {
          brand: newProduct.element_settings.brand,
        });
      }
    }
  },
  { immediate: true }
);

const pictogram = computed({
  get: () => {
    // Toujours prioriser le store de customisation
    return customization.value.pictogram || null;
  },
  set: (value) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, {
        pictogram: value,
      });
    }
  },
});

const pictoScale = computed({
  get: () => {
    if (!props.product) {
      return 1;
    }

    // Si nous avons une personnalisation différente de la valeur par défaut, l'utiliser
    if (
      customization.value.pictoScale &&
      customization.value.pictoScale !== 1
    ) {
      return customization.value.pictoScale;
    }

    // Sinon, utiliser les paramètres du produit
    return props.product?.element_settings?.pictoScale || 1;
  },
  set: (value) => {
    if (props.product?.id) {
      // Si la valeur est la même que celle des paramètres du produit, réinitialiser la personnalisation
      if (value === props.product?.element_settings?.pictoScale) {
        customizationStore.setCustomization(props.product.id, {
          pictoScale: 1, // Valeur par défaut
        });
      } else {
        customizationStore.setCustomization(props.product.id, {
          pictoScale: value,
        });
      }
    }
  },
});

const brand = computed({
  get: () => {
    // Toujours prioriser le store de customisation
    return customization.value.brand || null;
  },
  set: (value) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, { brand: value });
    }
  },
});

const brandScale = computed({
  get: () => {
    if (!props.product) {
      return 1;
    }

    // Si nous avons une personnalisation différente de la valeur par défaut, l'utiliser
    if (
      customization.value.brandScale &&
      customization.value.brandScale !== 1
    ) {
      return customization.value.brandScale;
    }

    // Sinon, utiliser les paramètres du produit
    return props.product?.element_settings?.brandScale || 1;
  },
  set: (value) => {
    if (props.product?.id) {
      // Si la valeur est la même que celle des paramètres du produit, réinitialiser la personnalisation
      if (value === props.product?.element_settings?.brandScale) {
        customizationStore.setCustomization(props.product.id, {
          brandScale: 1, // Valeur par défaut
        });
      } else {
        customizationStore.setCustomization(props.product.id, {
          brandScale: value,
        });
      }
    }
  },
});

const header = computed({
  get: () => customization.value.header || null,
  set: (value) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, { header: value });
    }
  },
});

const titleScale = computed({
  get: () => {
    if (!props.product) {
      return 1;
    }

    // Si nous avons une personnalisation différente de la valeur par défaut, l'utiliser
    if (
      customization.value.titleScale &&
      customization.value.titleScale !== 1
    ) {
      return customization.value.titleScale;
    }

    // Sinon, utiliser les paramètres du produit
    return props.product?.element_settings?.titleScale || 1;
  },
  set: (value) => {
    if (props.product?.id) {
      // Si la valeur est la même que celle des paramètres du produit, réinitialiser la personnalisation
      if (value === props.product?.element_settings?.titleScale) {
        customizationStore.setCustomization(props.product.id, {
          titleScale: 1, // Valeur par défaut
        });
      } else {
        customizationStore.setCustomization(props.product.id, {
          titleScale: value,
        });
      }
    }
  },
});

const priceScale = computed({
  get: () => {
    if (!props.product) {
      return 1;
    }

    // Si nous avons une personnalisation différente de la valeur par défaut, l'utiliser
    if (
      customization.value.priceScale &&
      customization.value.priceScale !== 1
    ) {
      return customization.value.priceScale;
    }

    // Sinon, utiliser les paramètres du produit
    return props.product?.element_settings?.priceScale || 1;
  },
  set: (value) => {
    if (props.product?.id) {
      // Si la valeur est la même que celle des paramètres du produit, réinitialiser la personnalisation
      if (value === props.product?.element_settings?.priceScale) {
        customizationStore.setCustomization(props.product.id, {
          priceScale: 1, // Valeur par défaut
        });
      } else {
        customizationStore.setCustomization(props.product.id, {
          priceScale: value,
        });
      }
    }
  },
});

const predefinedColors = [
  { name: "Gris clair", value: "#f8f8f8" },
  { name: "Blanc", value: "#ffffff" },
  { name: "Noir", value: "#000000" },
  { name: "Rouge AGCO", value: "#B70018" },
  { name: "Gris foncé", value: "#333333" },
  { name: "Transparent", value: "transparent" },
];

// Supprimer l'initialisation des valeurs locales


// Mettre à jour les watchers pour la taille du texte
watch(
  () => props.product?.name,
  () => {
    nextTick(() => {
      adjustTextSize(titleRef.value as unknown as HTMLElement, titleScale);
    });
  }
);

watch(
  () => props.product?.price_ht,
  () => {
    nextTick(() => {
      adjustTextSize(priceRef.value as unknown as HTMLElement, priceScale);
    });
  }
);

// Fonction pour ajuster la taille du texte
const adjustTextSize = (element: HTMLElement | null, scale: Ref<number>) => {
  if (!element) return;

  const parent = element.parentElement;
  if (!parent) return;

  const parentWidth = parent.offsetWidth;
  scale.value = 1;

  while (element.scrollWidth > parentWidth && scale.value > 0.4) {
    scale.value -= 0.05;
  }
};

const updateTitle = (event: Event) => {
  const target = event.target as HTMLElement;
  if (props.product) {
    emit("update:product", {
      ...props.product,
      name: target.innerText,
    });
    emitProductData();
  }
};

const updateDescription = (event: Event) => {
  const target = event.target as HTMLElement;
  if (props.product) {
    // Récupérer le contenu HTML et convertir les <br> en sauts de ligne
    const htmlContent = target.innerHTML;
    const textContent = htmlContent
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]*>/g, "") // Supprimer toutes les autres balises HTML
      .trim();

    emit("update:product", {
      ...props.product,
      description: textContent,
    });
    emitProductData();
  }
};

const handleEnterKey = (event: KeyboardEvent) => {
  event.preventDefault();
  const target = event.target as HTMLElement;
  const selection = window.getSelection();
  const range = selection?.getRangeAt(0);

  if (range) {
    const br = document.createElement("br");
    range.deleteContents();
    range.insertNode(br);
    range.setStartAfter(br);
    range.setEndAfter(br);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
};

const handlePriceEnter = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement;
  target.blur();
};

const updatePrice = (event: Event) => {
  const target = event.target as HTMLElement;
  if (props.product) {
    const priceText = target.innerText.replace(/[^0-9.,]/g, "");
    const price = parseFloat(priceText.replace(",", "."));

    if (!isNaN(price)) {
      const newPrice = Math.min(price, 999999.99);
      emit("update:product", {
        ...props.product,
        price_ht: newPrice,
      });
      emitProductData();
      target.innerText = formatPrice(newPrice);
    } else {
      target.innerText = formatPrice(props.product.price_ht);
    }
  }
};

const updateOldPrice = (event: Event) => {
  const target = event.target as HTMLElement;
  if (props.product) {
    const priceText = target.innerText.replace(/[^0-9.,]/g, "");
    const price = parseFloat(priceText.replace(",", "."));

    if (!isNaN(price)) {
      const newPrice = Math.min(price, 999999.99);
      emit("update:product", {
        ...props.product,
        price_old: newPrice,
      });
      emitProductData();
      target.innerText = formatPrice(newPrice);
    } else {
      target.innerText = formatPrice(props.product.price_old || 0);
    }
  }
};

const formatPrice = (price: number | null) => {
  if (price === null) return "0,00";
  return price.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPriceTTC = (price: number | null) => {
  if (price === null) return "0,00";
  const ttc = price * 1.2; // TVA 20%
  return ttc.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDescription = (description: string) => {
  if (!description) return "";
  // Convertir les sauts de ligne en balises <br>
  return description.replace(/\n/g, "<br>");
};

// URL helpers
const apiUrl = import.meta.env.VITE_API_URL;

const getBrandImageUrl = (imageName: string) => {
  return `${apiUrl}/upl/brands/${imageName}`;
};

const getPictoImageUrl = (imageName: string) => {
  return `${apiUrl}/upl/pictos/${imageName}`;
};

const getHeaderImageUrl = (imageName: string) => {
  return `${apiUrl}/upl/headers/${imageName}`;
};

// Drop handlers
const onBrandDrop = (event: DragEvent) => {
  const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");
  if (data.type === "brand") {
    brand.value = data.data;
  }
};

const onPictoDrop = (event: DragEvent) => {
  const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");
  if (data.type === "pictogram") {
    pictogram.value = data.data;
  }
};

const onProductDrop = (event: DragEvent) => {
  try {
    const data = JSON.parse(event.dataTransfer?.getData("text/plain") || "{}");
    if (data.type === "product" && data.data) {
      emit("update:product", {
        ...data.data,
        product_image_id: data.data.product_image_id,
      });
    }
  } catch (error) {
    console.error("Erreur lors du drop du produit:", error);
  }
};

const toggleBackground = () => {
  removeBackground.value = !removeBackground.value;
};

const adjustPictoSize = (delta: number) => {
  if (!props.product?.id) return;

  // Récupérer la personnalisation actuelle
  const currentCustomization = customizationStore.getCustomization(
    props.product.id
  );
  const currentScale = currentCustomization.pictoScale || 1;
  const newScale = currentScale + delta;

  customizationStore.setCustomization(props.product.id, {
    ...currentCustomization,
    pictoScale: newScale,
  });
  emitProductData();
};

const toggleColorPicker = () => {
  showColorPicker.value = !showColorPicker.value;
};

const selectBackgroundColor = (color: string) => {
  backgroundColor.value = color;
  customColor.value = color;
  showColorPicker.value = false;
};

const removePicto = () => {
  pictogram.value = null;
};

const resetProductData = () => {
  brand.value = null;
  pictogram.value = null;
  header.value = null;
  removeBackground.value = false;
  pictoScale.value = 1;
  showColorPicker.value = false;
  backgroundColor.value = "#f8f8f8";
  customColor.value = "#f8f8f8";
};

// Fermer le color picker quand on clique ailleurs
onMounted(() => {
  document.addEventListener("click", (event) => {
    if (showColorPicker.value) {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".color-picker") &&
        !target.closest(".background-color-toggle")
      ) {
        showColorPicker.value = false;
      }
    }
  });
});

const adjustPriceSize = (delta: number) => {
  const newScale = priceScale.value + delta;
  priceScale.value = newScale;
  // Émettre le changement vers le parent
  if (props.product?.id) {
    emit("update:product", {
      ...props.product,
      element_settings: {
        ...props.product.element_settings,
        priceScale: newScale,
      },
    });
  }
};

const adjustTitleSize = (delta: number) => {
  const newScale = titleScale.value + delta;
  titleScale.value = newScale;
  // Émettre le changement vers le parent
  if (props.product?.id) {
    emit("update:product", {
      ...props.product,
      element_settings: {
        ...props.product.element_settings,
        titleScale: newScale,
      },
    });
  }
};

const getProductImageUrl = (imageNameOrPath: string) => {
  if (imageNameOrPath.startsWith('products/')) {
    return `${apiUrl}/upl/${imageNameOrPath}`;
  }
  return `${apiUrl}/upl/products/${imageNameOrPath}`;
};

// Ajouter cette nouvelle méthode
const emitProductData = () => {
  if (props.product) {
    const productData = {
      ...props.product,
      element_settings: {
        ...customization.value,
        price_ht: props.product.price_ht,
        price_ttc: props.product.price_ht * 1.2, // TVA 20%
      },
    };
    emit("update:productData", productData);
  }
};

// Ajouter des watchers pour les changements de pictogram et brand
watch(pictogram, () => {
  emitProductData();
});

watch(brand, () => {
  emitProductData();
});

// Appeler drawToCanvas quand le produit change
watch(
  () => props.product,
  () => {
    drawToCanvas();
  },
  { deep: true }
);

// Appeler drawToCanvas au montage
onMounted(() => {
  drawToCanvas();
});

const adjustBrandSize = (delta: number) => {
  if (!props.product?.id) return;

  // Récupérer la personnalisation actuelle
  const currentCustomization = customizationStore.getCustomization(
    props.product.id
  );
  const currentScale = currentCustomization.brandScale || 1;
  const newScale = currentScale + delta;

  customizationStore.setCustomization(props.product.id, {
    ...currentCustomization,
    brandScale: newScale,
  });
  // Émettre les changements pour mettre à jour le composant parent
  emitProductData();
};

const removeBrand = () => {
  brand.value = null;
};

// Utiliser les données du store pour showOldPrice
const showOldPriceComputed = computed({
  get: () => {
    if (!props.product?.id) return false;
    return customization.value.showOldPrice || false;
  },
  set: (value) => {
    if (props.product?.id) {
      customizationStore.setCustomization(props.product.id, {
        showOldPrice: value,
      });
    }
  },
});

const toggleOldPrice = () => {
  const willShow = !showOldPriceComputed.value;
  showOldPriceComputed.value = willShow;

  if (willShow && props.product && props.product.price_old === undefined) {
    emit("update:product", {
      ...props.product,
      price_old: props.product.price_ht || 0,
    });
  }
};

// Ajouter un watcher pour showOldPrice
watch(showOldPriceComputed, () => {
  emitProductData();
});

// Ajouter cette méthode pour dessiner sur le canvas
const drawToCanvas = async () => {
  await nextTick();
  const canvas = document.getElementById("page-1") as HTMLCanvasElement;
  if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
    // console.warn("Canvas element not found or invalid");
    return;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    console.warn("Could not get 2D context from canvas");
    return;
  }

  // Effacer le canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Dessiner le fond
  ctx.fillStyle = backgroundColor.value;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Dessiner l'image du produit si elle existe
  if (props.product?.product_image?.image_path || props.product?.product_image?.image_name || props.product?.image) {
    const img = new Image();
    img.src = props.product?.product_image?.image_path || props.product?.product_image?.image_name
      ? getProductImageUrl(props.product.product_image.image_path || props.product.product_image.image_name)
      : props.product?.image;

    await new Promise((resolve) => {
      img.onload = () => {
        const scale = Math.min(
          canvas.width / img.width,
          canvas.height / img.height
        );
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;

        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        resolve(null);
      };
    });
  }

  // Dessiner le titre
  if (props.product?.name) {
    ctx.fillStyle = "#000000";
    ctx.font = "bold 24px Arial";
    ctx.textAlign = "center";
    ctx.fillText(props.product.name, canvas.width / 2, 50);
  }

  // Dessiner la description
  if (props.product?.description) {
    ctx.fillStyle = "#666666";
    ctx.font = "16px Arial";
    ctx.textAlign = "center";
    const words = props.product.description.split(" ");
    let line = "";
    let y = 100;

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      const metrics = ctx.measureText(testLine);

      if (metrics.width > canvas.width - 40) {
        ctx.fillText(line, canvas.width / 2, y);
        line = words[i] + " ";
        y += 20;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, canvas.width / 2, y);
  }

  // Dessiner le prix
  if (props.product?.price_ht) {
    ctx.fillStyle = "#B70018";
    ctx.font = "bold 32px Arial";
    ctx.textAlign = "center";
    ctx.fillText(
      `${formatPrice(props.product.price_ht)} € HT`,
      canvas.width / 2,
      canvas.height - 50
    );
  }
};

// Appeler drawToCanvas quand le produit change
watch(
  () => props.product,
  () => {
    drawToCanvas();
  },
  { deep: true }
);

// Appeler drawToCanvas au montage
onMounted(() => {
  drawToCanvas();
});
</script>

<script lang="ts">
export default {
  name: "ProductBlock",
};
</script>

<style scoped>
.product-block {
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header-zone {
  width: 100%;
  overflow: hidden;
}

.product-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  gap: 0.5rem;
  min-height: 0; /* Important pour le flex */
}

.brand-zone {
  position: absolute !important;
  bottom: 1rem !important;
  right: 2.5rem !important;
  width: 4rem !important;
  height: 2rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent !important;
  border: 2px dashed #ccc !important;
  border-radius: 0.5rem !important;
  padding: 0.25rem !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  z-index: 20 !important;
}

.preview-mode .brand-zone {
  border: none !important;
  background: transparent !important;
}

.brand-zone:hover {
  border-color: #b70018 !important;
  background: rgba(183, 0, 24, 0.05) !important;
}

.brand-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
}

.product-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0; /* Important pour le flex */
  height: 100%;
}

.image-container {
  flex: 1;
  min-height: 0;
  width: 100%;
  background: #f8f8f8;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  max-height: 45%;
  min-height: 150px;
  padding: 0.5rem;
}

.product-image {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background: transparent;
  transition: mix-blend-mode 0.3s ease;
  transform: scale(1);
}

.product-image.remove-background {
  mix-blend-mode: multiply;
}

.empty-state {
  color: #999;
  font-size: 0.875rem;
}

/* Styles pour l'état vide en mode aperçu */
.empty-state-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8; /* Correspond au fond image-container */
}

.default-product-svg {
  width: 60%; /* Ajustez la taille si besoin */
  height: 60%; /* Ajustez la taille si besoin */
  max-width: 100px;
  max-height: 100px;
}

.product-info {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  justify-content: space-between;
  height: 100%;
}

.product-title {
  font-weight: 600;
  line-height: 1.2;
  font-size: 1.4rem;
  min-height: 1.2em;
  max-height: 2.4em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: block;
}

.product-description {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.3;
  min-height: 2.6em;
  max-height: 3.9em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  margin-bottom: 0.5rem;
  position: relative;
}

.product-description:focus {
  z-index: 60;
  overflow: visible;
  display: block;
  -webkit-line-clamp: unset;
  max-height: none;
}

.product-description.empty-description {
  color: transparent;
  user-select: none;
}

.price-section {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 2.2em;
  height: 2.2em;
}

.price-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
  z-index: 10;
}

.price-content {
  transition: opacity 0.2s ease;
}

.price-content.hidden {
  opacity: 0;
  height: 0;
  overflow: hidden;
}

.product-price {
  flex-grow: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.price-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: flex-start;
}

.price-ht-container {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  transform-origin: left;
  transform: scale(v-bind(priceScale));
}

.price-ht {
  font-weight: 600;
  color: rgb(var(--price-color));
  display: inline-block;
}

.price-suffix.ht {
  color: rgb(var(--price-color));
  font-weight: 500;
  white-space: nowrap;
  display: inline-block;
  /* margin-left: 0.5rem; */
}

.price-controls {
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
  z-index: 10;
}

.title-container {
  position: relative;
  display: inline-block;
}

.title-controls {
  position: absolute;
  top: -2.5rem;
  right: 0;
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  z-index: 50;
}

.price-ttc-container {
  margin-top: 0.02rem;
  transform-origin: left;
  transform: scale(v-bind(priceScale));
}

.price-ttc {
  color: #666;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.ttc-text {
  /* margin-left: 0.25rem; */
}

/* Tailles de prix */
.price-sm {
  font-size: 1.2rem;
}

.price-md {
  font-size: 1.6rem;
}

.price-lg {
  font-size: 2rem;
}

/* Tailles de prix TTC */
.price-ttc-sm {
  font-size: 0.6rem;
}

.price-ttc-md {
  font-size: 0.6rem;
}

.price-ttc-lg {
  font-size: 0.6rem;
}

/* Variants */
.columnOne .product-content,
.columnTwo .product-content {
  padding: 0.5rem;
}

.columnOne .image-container,
.columnTwo .image-container {
  flex: 1;
  min-height: 0;
  max-height: 60%;
  min-height: 180px;
  padding: 0.5rem;
}

.columnOne .product-info,
.columnTwo .product-info {
  flex: 1 1 100%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.rowOne .product-content,
.rowTwo .product-content {
  flex-direction: row-reverse;
  gap: 0.75rem;
  align-items: center;
}
.rowTwo .product-content .product-main {
  display: flex;
  flex-direction: row-reverse;
  height: 100%;
  min-height: 0;
  gap: 0.75rem;
}

.rowTwo .product-content .product-main .image-container {
  flex: 1 1 0;
  max-width: 50%;
  max-height: 100%;
  min-width: 120px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  -o-object-fit: contain;
  object-fit: contain;
  height: auto;
}

.rowTwo .product-info {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  flex: 1 !important;
  padding-left: 2rem !important;
}

.rowTwo .product-info .product-title {
  font-size: 1.6rem;
  margin-bottom: 0.75rem;

  font-weight: 600;
  line-height: 1.2;
  min-height: 1.2em;
}

.rowTwo .product-info .product-description {
  font-size: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  margin-bottom: 3.5rem;
  height: 100%;
  min-height: 30%;
}

.rowTwo .product-info .price-ht {
  font-size: 1.6rem;
}

.rowTwo .product-info .price-ttc {
  font-size: 0.9rem;
}

.rowOne .image-container,
.rowTwo .image-container {
  flex: 1;
  max-width: 50%;
  height: 100%;
  padding: 0.5rem;
  min-height: 150px;
}

.rowOne .product-info,
.rowTwo .product-info {
  flex: 1;
  justify-content: center;
  padding-right: 1rem;
}

.rowOne.is-main .product-content,
.rowTwo.is-main .product-content {
  padding: 1rem;
}

.rowOne.is-main .image-container,
.rowTwo.is-main .image-container {
  max-width: 100%;
  /* padding: 1rem; */
  min-height: 250px;
}

.rowOne.is-main .product-info,
.rowTwo.is-main .product-info {
  flex: 1;
  padding-right: 2rem;
}

.is-main .product-title {
  font-size: 1.6rem;
  margin-bottom: 0.75rem;
}

.is-main .product-description {
  font-size: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  margin-bottom: 3.5rem;
  height: 100%;
  min-height: 30%;
}

.is-main .price-ht {
  font-size: 1.6rem;
}

.is-main .price-ttc {
  font-size: 0.9rem;
}

.is-main .image-container {
  max-height: 78%;
  min-height: 570px;
}

/* Styles spécifiques pour les layouts avec 4, 5 et 6 produits */
.products-container[class*="grid-4"] .is-main .product-title,
.products-container[class*="grid-5"] .is-main .product-title,
.products-container[class*="grid-6"] .is-main .product-title {
  font-size: 1.6rem;
  margin-bottom: auto;
}

.products-container[class*="grid-4"] .is-main .product-description,
.products-container[class*="grid-6"] .is-main .product-description {
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.products-container[class*="grid-4"] .is-main .price-ht,
.products-container[class*="grid-5"] .is-main .price-ht,
.products-container[class*="grid-6"] .is-main .price-ht {
  font-size: 1.4rem;
}

.products-container[class*="grid-4"] .is-main .price-ttc,
.products-container[class*="grid-5"] .is-main .price-ttc,
.products-container[class*="grid-6"] .is-main .price-ttc {
  font-size: 0.6rem;
}

.products-container[class*="grid-4"] .is-main .image-container,
.products-container[class*="grid-5"] .is-main .image-container,
.products-container[class*="grid-6"] .is-main .image-container {
  max-height: 50%;
  min-height: 200px;
}

.remove-button {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #b70018;
  z-index: 10;
}

.picto-zone {
  position: absolute;
  top: 0rem;
  right: 0rem;
  width: 10rem;
  height: 4rem;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  background: transparent;
  border: 1px dashed #ccc;
  border-radius: 0.5rem;
  padding: 0 0.5rem 0.5rem 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
}

.picto-zone:hover {
  background: transparent;
  border-color: #b70018;
}

.picto-image {
  width: auto;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
  transition: transform 0.2s ease;
  transform-origin: top right;
}

.picto-controls {
  position: absolute;
  top: 4rem;
  right: 0;
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  z-index: 50;
}

.size-control {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.2s ease;
}

.size-control:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.background-toggle {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.background-toggle:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.background-toggle .icon {
  font-size: 1.2rem;
}

.background-color-toggle {
  position: absolute;
  bottom: 1rem;
  left: 4rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.background-color-toggle:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.color-picker {
  position: absolute;
  bottom: 4rem;
  left: 4rem;
  background: white;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  padding: 0.5rem;
  z-index: 20;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.color-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.color-option {
  width: 2rem;
  height: 2rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.color-option:hover {
  transform: scale(1.1);
  border-color: #b70018;
}

.custom-color-input {
  width: 100%;
  height: 2rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  cursor: pointer;
  padding: 0;
}

/* Les règles de mode aperçu ont été déplacées dans SupportCanvas.vue */

.toggle-price-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: 0.1rem;
  font-size: 1rem;
  z-index: 5;
  color: #666;
  flex-shrink: 0;
}

.columnOne .product-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
}

.columnOne .image-container {
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: max-height 0.2s;
  max-height: 75%;
  padding: 0.25rem;
}

.columnOne .product-info {
  flex: 1 1 auto;
  min-height: 80px;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.columnOne .brand-zone {
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  left: "" 0.2rem;
  width: auto;
  border: 2px dashed #ccc;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: transparent;
  border: none;
  z-index: 10;
}

.rowOne .product-main {
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  gap: 0.75rem;
}

.rowOne .image-container {
  flex: 1 1 0;
  max-width: 50%;
  max-height: 100%;
  min-width: 120px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: contain;
  height: auto;
}

.rowOne .product-info {
  flex: 2 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.brand-controls {
  position: absolute;
  top: -2rem;
  right: 0;
  display: flex;
  gap: 0.25rem;
  background: white;
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
  z-index: 50;
}

.remove-brand {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: all 0.2s ease;
}

.remove-brand:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.columnOne .product-title {
  font-size: 1.4rem;
}

.columnOne .product-description {
  font-size: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  margin-bottom: 1.5rem;
}

.columnOne .price-ht {
  font-size: 1.1rem;
}

.columnOne .price-ttc {
  font-size: 0.6rem;
}

.brand-placeholder {
  color: #999;
  font-size: 0.9rem;
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.products-container[class*="grid-4"] .is-main .product-info {
  min-height: 140px;
  height: 100%;
  justify-content: space-between;
}
.products-container.grid-4 .is-main .product-title {
  font-size: 1.5rem;
  min-height: 1.3em;
  max-height: 2.6em;
  margin-bottom: 0.2em;
}
.products-container.grid-4 .is-main .product-description {
  -webkit-line-clamp: 4;
  /* min-height: 3.5em;
  max-height: 5.2em; */
}
.products-container.grid-4 .is-main .price-section {
  min-height: 2.2em;
  height: 2.2em;
  justify-content: flex-end;
  margin-top: 0.75em;
}

.products-container.grid-4 .product-zone:not(.is-main) .product-description {
  -webkit-line-clamp: 4;
  /* min-height: 3.5em;
  max-height: 5.2em; */
}

.products-container.grid-5 .is-main .product-title {
  font-size: 1.2rem;
  min-height: 1.1em;
  max-height: 1.3em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
.products-container.grid-5 .is-main .product-description {
  -webkit-line-clamp: 4;
  min-height: auto !important;
  max-height: auto !important;
}
.products-container.grid-5 .product-zone:not(.is-main) .product-description {
  -webkit-line-clamp: 3;
  /* min-height: calc(1.3em * 4); */
  max-height: calc(1.3em * 4);
}

.price-old-container {
  /* margin-bottom: 0.25rem; */
  transform-origin: left;
  transform: scale(v-bind(priceScale));
}

.price-old {
  font-size: 0.6rem;
  font-weight: 600;
  /* margin-bottom: 0.5rem; */
  color: #999;
  text-decoration: line-through;
  display: inline-block;
}

.price-old-container .price-suffix {
  color: #999;
  font-size: 0.6rem;
}

.toggle-old-price-button {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background: white;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: bold;
  transition: all 0.2s ease;
}

.toggle-old-price-button:hover {
  border-color: #b70018;
  background: #f8f8f8;
}

.toggle-old-price-button .icon {
  font-size: 0.7rem;
}
</style>
